import { DocumentsSectionsState } from '../../+grid-application-shared/entities/sections-state/documentsSectionsState';
import { PersonalDataSectionsState } from '../../+grid-application-shared/entities/sections-state/personalDataSectionsState';
import { TechnicalSpecificationsSectionsState } from '../../+grid-application-shared/entities/sections-state/technicalSpecificationsSectionsState';
import {
  ContactPerson,
  Document,
  FeedInInstallationDataObject,
  GridCompTechData,
  LookupOptionsData,
  PowerPlantOperatorAddress,
  UpdateBusinessPartnerData,
  UpdateConnectionObjectData,
  UpdateInvoiceAddressData,
} from '../jurisdiction.domain';

export interface InstallationData {
  installation_id?: string;
  application_id?: string;
  measuring_point_company?: string;
  sk_inst_change_type?: string;
  sk_inst_change_type_gas?: string;
  sk_inst_change_type_water?: string;
  sk_inst_change_type_districtheat?: string;
  sk_measuring_point_operator?: string;
  replaced_meter_no?: string;
  replaced_meter_reading?: string;
  replaced_meter_image_id?: string;
  confirm_chimneysweeper_report?: string;
  confirm_g600?: string;
  confirm_ndav?: string;
  sk_chim_sweep_confirm?: string;
  sk_sitecon_current?: string;
}

export interface FacilityInstallationResponse {
  installation_id: string;
  application_id: string;
  measuring_point_company?: string;
  sk_measuring_point_operator?: string;
  sk_inst_change_type?: string;
  sk_inst_change_type_gas?: string;
  sk_inst_change_type_water?: string;
  sk_inst_change_type_districtheat?: string;
  facility?: any[];
  fireingsystem: GasFiringSystemData;
  meter?: any[];
  old_meter?: any[];
  sk_chim_sweep_confirm?: string;
  sk_sitecon_current?: string;
  confirm_chimneysweeper_report?: string;
  confirm_g600?: string;
  confirm_ndav?: string;
}

export interface GasDeviceObject {
  deviceId: string;
  facilityId: string;
  ceNumber: string;
  power: string;
  deviceRemovedCheckbox: boolean;
  selectedGasCategory: string;
  customFields: any;
  type: string;
  deviceName?: string;
  requiredFlowPressure?: string;
  applianceType?: LookupOptionsData;
  manufacturer?: string;
  manufactureYear?: string;
  applianceCategory?: string;
  otherManufacturer?: string;
  otherFlowPressure?: string;
  gasUsageFor?: string;
}

export interface WaterDeviceObject {
  deviceId: string;
  facilityId: string;
  specialApplianceDropDown: string;
  amount?: string;
  vrCold?: string;
  vrTotalVolume?: number;
  vrHot?: string;
  swknManufacturer?: string;
  swknDWGVCertifiedCheckbox?: boolean;
  swknType?: string;
  swknFlowRate?: string;
  swknDeliveryPressure?: string;
  swknReqExtinguishingVolume?: string;
  swknWaterFacilitySystemType?: string;
  customFields: any;
}

export interface BaseDataObject {
  facilityId: string;
  installationId: string;
  facilityType: string;
  noOfConnections: string;
  placeOfMeter: string;
  gasUsageFor?: string;
  gasDevice?: GasDeviceObject[];
  sector?: string;
  registration?: string;
  internalConsumptionCheckbox?: boolean;
  storageSystemCheckbox?: boolean;
  specialAppliance?: any[];
  sumFlowRate?: string;
  peakFlowRate?: string;
  heatingDevice?: HeatingDeviceObject;
  waterDevice?: any[];
  otherLocation: string;
  otherType?: string;
  measuringPointCheckbox?: boolean;
  oldPowerGridConnection?: string;
  newPowerGridConnection?: string;
  totalHeatOutput?: string;
  isPowerConnectionForRemoteMeterReadingAvailable?: string;

  waterPipeMaterial?: string;
  waterInstallationType?: string;
  waterRefluxFuse?: string;
  waterDevices?: string;
  oldMeterNumber?: string;

  simultaneouslyRequiredTotalPowerAfterInstallationChange?: number;

  meterLocationDesignation?: string;
  reasonForRequest?: string;
  heatedLivingSpace?: number;
  volumeFlowQmax?: number;
  flowTemperature?: number;
  returnTemperature?: number;
  measuringConcept?: string;
  typeOfMeter?: string;
  vrTotalVolume?: number;
  specialStructure?: string;
}

export interface FiringSystemDataObject {
  systemId?: string;
  constructionChimneySweep: string;
  chimneyCheckbox: boolean;
  singleOccupancyCheckbox: boolean;
  vacuumOperationCheckbox: boolean;
  moistureSensitiveCheckbox: boolean;
  notMoistureSensitiveCheckbox: boolean;
  emissionGasLineCheckbox: boolean;
  overPressureOperationCheckbox: boolean;
  multipleOccupancyCheckbox: boolean;
  airEmissionSystemCheckbox: boolean;
  assemblyLineCheckbox: boolean;
  systemUsabilityCheckbox: boolean;
  systemUsabilityTextValue: string;
  effectiveHeight: string;
  chimney: string;
  fireplace: string;
  dimension: string;
  din: string;
  withBlowerCheckbox: boolean;
  withoutBlowerCheckbox: boolean;
  withFlowProtectionCheckbox: boolean;
  withoutFlowProtectionCheckbox: boolean;
  airSupplyOpenAirCheckbox: boolean;
  airSupplyInstallationRoomCheckbox: boolean;
  emissionGasCheckbox: boolean;
  roomVentilation: string;
  roomVentilationValueincm: string;
  approvalClient: boolean;
  approvalPlanner: boolean;
  meterInstallation: string;
  innerShell?: BuildingData;
}

export interface FurnaceDataObject {
  furnaceId?: string;
  systemId?: string;
  temp: string;
  proofOfUsability: string;
  heat: string;
  setHeat: string;
  volume: string;
  fuel: string;
  producerType: string;
  furnace: string;
  otherFuelType: string;
}

export interface BuildingData {
  buildingMaterial?: string;
  thicknessInCm?: string;
  producerType?: string;
  proofOfUsability?: string;
}

export interface FacilityDataObject {
  installationId: string;
  measuringPointOperator: string;
  company: string;
}

export interface MeterDataObject {
  meterId: string;
  installationId: string;
  meterType?: string;
  type?: string;
  amount?: string;
  swknFloor?: string;
  selectMount?: string;
  selectComm?: string;
  transformerFactor?: string;
  providedByCustomerCheckbox?: boolean;
  customerName?: string;
  providedByMsbCheckbox?: boolean;
  msbName?: string;
  counterSize?: string;
  modernMeterSubtype?: string;
  annualConsumption?: string;
  annualConsumptionForecast?: string;
  otherType?: string;
  meterCompliance?: string;
  alignment?: string;
  requestedTotalPower?: string;
  remoteReadoutCheckbox?: boolean;
  controlUnitAvailableCheckbox?: boolean;
  swknRemoteReadoutCheckbox?: boolean;
  meterThread?: string;
  meterFlange?: string;
  overAllLength?: string;
  sensorLength?: string;
  sensorThread?: string;
  oldMeterNumber?: string;
  transducerSize?: string;
  location?: string;
  rlmMeasurement?: string;
  meterNumber?: string;
  meterReading?: string;
  meterImageId?: string;
  meterAction?: string;
  concept?: string;
  remoteReadoutOption?: string;
  maxSimRefPower?: string;
}

export interface HeatingDeviceObject {
  dhType: string;
  dhTypeHeating: string;
  heatingLoad: string;
  installHeatPower: string;
  floorHeating: string;
  dhw: boolean;
  designTempTv: string;
  dhwType: string;
  designTempFloorHeatingTv: string;
  dhwvolume: string;
  designTempTr: string;
  designTempFloorHeatingTr: string;
  thermoValveType: string;
  flowRadiators: string;
  deviation: string;
  flowFloorHeating: string;
  installDirections: string;
  customFields: Object;
}

export interface GetInstallationApplicationObject {
  installationapplication: InstallationApplicationData[];
}

export interface InstallationApplicationData {
  application_id: string;
  application_ext_id?: string;
  building_con_from?: string;
  comment: string;
  installation_comment?: string;
  desired_date: string;
  technician_id: string;
  application_date: string;
  objective: string;
  sk_installation_type: string;
  sk_status: string;
  businesspartner: UpdateBusinessPartnerData;
  connectionobject: UpdateConnectionObjectData;
  powerinstallation: FacilityInstallationResponse;
  gasinstallation: FacilityInstallationResponse;
  waterinstallation: FacilityInstallationResponse;
  districtheatinstallation: FacilityInstallationResponse;
  document?: Document[];
  feedininstallation?: FeedInInstallationDataObject;
  applicant_is_landowner?: string;
  applicant_is_invoicerecipient?: string;
  invoicerecipient?: UpdateInvoiceAddressData;
  confirm_chimneysweeper_report?: string;
  confirm_g600?: string;
  confirm_ndav?: string;
  chimney_sweeper_id?: string;
  location?: Location;
  user_id?: string;
  contact_person?: ContactPerson;
}

export interface Location {
  id: number;
  location_object?: string;
}

export interface GetGridCompFeedInApplicationObject {
  application: GridCompFeedInApplicationData[];
}

export interface GridCompFeedInApplicationData {
  application_id: string;
  application_ext_id?: string;
  user_id: string;
  building_con_from?: string;
  comment: string;
  installation_comment?: string;
  desired_date: string;
  technician_id: string;
  application_date: string;
  objective: string;
  sk_installation_type: string;
  sk_status: string;
  sk_type: string;
  confirm_chimneysweeper_report?: string;
  confirm_g600?: string;
  confirm_ndav?: string;
  chimney_sweeper_id?: string;
  applicant_is_landowner?: string;
  applicant_is_invoicerecipient?: string;
  sk_application_type: string;
  last_changed: string;
  determinated_price: string;
  archived: string;
  locked: string;
  lockedby: string;
  technical_conditions_met: string;

  businesspartner: UpdateBusinessPartnerData;
  connectionobject: UpdateConnectionObjectData;
  technical_data: GridCompTechData;
  app_power_plant_operator_address: PowerPlantOperatorAddress;
  invoicerecipient?: UpdateInvoiceAddressData;
  document?: Document[];

  technical_specifications_sections_state: TechnicalSpecificationsSectionsState;
  personal_data_sections_state: PersonalDataSectionsState;
  documents_sections_state: DocumentsSectionsState;
}

//#region "data models for Gas Facility forms"
export interface GasInstallationResponse {
  gasinstallation: {
    installation_id: string;
  }[];
}

export interface GasFacilityData {
  facility_id?: string;
  installation_id?: string;
  transaction_no: string;
  sk_gas_facility_type: string;
  sk_gas_usage_for?: string;
  sk_device_location: string;
  commercial_sector: string;
  other_location_details: string;
  sk_chim_sweep_confirm: string;
  total_power_required_old?: string;
  total_power_required_new?: string;
  total_power?: string;
  power_supply_available?: string;
  old_meter_number?: string;
  reason_for_request?: string;
}

export interface GasFurnaceData {
  system_id?: string;
  furnance_id?: string;
  manufacturer?: string;
  type?: string;
  proof_of_usability?: string;
  room_volume?: string;
  emission_temp?: string;
  sk_fuel_type?: string;
  rated_heat1?: string;
  rated_heat2?: string;
  combustion_other_fuel?: string;
}

export interface GasFiringSystemData {
  installation_id?: string;
  system_id?: string;
  furnance?: GasFurnaceData[];
  sk_construction_spec?: string;
  chimney?: string;
  single_occupancy?: string;
  vacuum_operation?: string;
  moisture_sensitive?: string;
  moisture_insensitive?: string;
  emission_gas_line?: string;
  overpressure_operation?: string;
  multiple_occupancy?: string;
  air_emission_system?: string;
  proof_of_usability_type?: string;
  proof_of_usability_comment?: string;
  assembly_line?: string;
  innershell_bulding_material?: string;
  insualtion_bulding_material?: string;
  ringgap_bulding_material?: string;
  outershell_bulding_material?: string;
  innershell_thickness?: string;
  insualtion_thickness?: string;
  ringgap_thickness?: string;
  sk_outershell_thickness?: string;
  innershell_manufacturer?: string;
  insualtion_manufacturer?: string;
  ringgap_manufacturer?: string;
  outershell_manufacturer?: string;
  innershell_proof_of_usability?: string;
  insualtion_proof_of_usability?: string;
  ringgap_proof_of_usability?: string;
  outershell_proof_of_usability?: string;
  effective_height?: string;
  pipe_width?: string;
  sk_pipe_with_unit?: string;
  sk_assessment_base?: string;
  assessment_base_din_comment?: string;
  combustion_with_ventilation?: string;
  combustion_without_ventilation?: string;
  combustion_with_flowprotection?: string;
  combustion_without_flowprotection?: string;
  combustion_air_supply_room?: string;
  combustion_air_supply_open?: string;
  combustion_exhaust_combair?: string;
  sk_combustion_boiler_ventilation_type?: string;
  combustion_boiler_ventilation?: string;
  written_approval_client?: string;
  written_approval_planner?: string;
  has_gasmeter_installation?: string;
}

export interface GasFacilityResponse {
  gasfacility: {
    facility_id: string;
  }[];
}

export interface UpdateGasFacilityResponse {
  gasfacility: GasFacilityData[];
}

export interface GasFiringSystemResponse {
  fireingsystem: {
    system_id: string;
  }[];
}

export interface UpdateGasFiringSystemResponse {
  fireingsystem: GasFiringSystemData[];
}

export interface GasDeviceData {
  device_id?: string;
  facility_id?: string;
  ce_number: string;
  name: string;
  power: string;
  device_removed: string;
  sk_selected_gas_category: string;
  gas_device_type: string;
  sk_device_manufacturer: string;
  device_manufacturer_other: string;
  type: string;
  construction_year: string;
  sk_device_category: string;
  sk_required_flow_pressure?: string;
  other_pressure?: string;
  sk_gas_usage_for?: string;
}

export interface GasDeviceResponse {
  gasdevice: {
    device_id: string;
  }[];
}

export interface UpdateGasDeviceResponse {
  gasdevice: GasDeviceData[];
}

export interface GasMeterData {
  meter_id?: string;
  installation_id?: string;
  meter_count: string;
  consumption_forecast: string;
  sk_gas_meter_type: string;
  sk_meter_mount?: string;
  sk_communication: string;
  sk_gas_meter_size: string;
  sk_mounting_alignment: string;
  remote_readout: string;
  position?: string;
  old_meter_number?: string;
}

export interface GasMeterResponse {
  gasmeter: {
    meter_id: string;
  }[];
}

export interface UpdateGasMeterResponse {
  gasmeter: GasMeterData[];
}

export interface GasFurnaceResponse {
  furnance: {
    furnance_id: string;
  }[];
}

export interface UpdateGasFurnaceResponse {
  furnance: GasFurnaceData[];
}

//#endregion "data models for Gas Facility forms"

//#region "data models for District Heating Facility forms"
export interface HeatingInstallationResponse {
  districtheatinstallation: {
    installation_id: string;
  }[];
}

export interface HeatingFacilityData {
  facility_id?: string;
  installation_id?: string;
  transaction_no: string;
  sk_districtheat_facility_type: string;
  sk_districtheat_device_location: string;
  sk_districtheat_distribution_type: string;
  sk_districtheat_heating_type: string;
  heating_load: string;
  heating_power: string;
  floor_heating_power: string;
  design_temperature_tv: string;
  floor_design_temperature_tv: string;
  design_temperature_tr: string;
  floor_design_temperature_tr: string;
  radiator_thermovalve_brand: string;
  swkn_districtheat_device_location_text: string;
  sk_swkn_districthead_system_type: string;
  radiator_deviation: string;
  radiator_flowrate: string;
  floor_fowrate: string;
  drinking_water: string;
  drinking_water_brand: string;
  drinking_water_volumen: string;
  commercial_sector?: string;
  other_location_details: string;
  old_meter_number?: string;
  reason_for_request?: string;
  heated_living_space?: number;
  volume_flow_qmax?: number;
  flow_temperature?: number;
  return_temperature?: number;
}

export interface HeatingFacilityResponse {
  districtheatfacility: {
    facility_id: string;
  }[];
}

export interface UpdateHeatingFacilityResponse {
  districtheatfacility: HeatingFacilityData[];
}

export interface HeatingMeterData {
  meter_id?: string;
  installation_id?: string;
  meter_count?: string;
  consumption_forecast: string;
  sk_districtheat_meter_type: string;
  sk_meter_mount: string;
  sk_communication: string;
  sk_heat_meter_size: string;
  sk_mounting_alignment: string;
  requested_total_power: string;
  meter_thread?: string;
  meter_flange?: string;
  meter_length?: string;
  sensor_length?: string;
  sensor_thread?: string;
}

export interface HeatingMeterResponse {
  districtheatmeter: {
    meter_id: string;
  }[];
}

export interface UpdateHeatingMeterResponse {
  districtheatmeter: HeatingMeterData[];
}

export interface MeterCharacteristicResponse {
  metercharacteristic: MeterCharacteristicData[];
}

export interface MeterCharacteristicData {
  hmchar_id: string;
  meter_size: string;
  meter_thread: string;
  meter_flange: string;
  meter_length: string;
  sensor_length: string;
  sensor_thread: string;
}

//#endregion "data models for District Heating Facility forms"

//#region "data models for Power Facility forms"
export interface PowerInstallationResponse {
  powerinstallation: {
    installation_id: string;
  }[];
}

export interface PowerFacilityData {
  facility_id?: string;
  installation_id?: string;
  sk_power_facility_type?: string;
  sk_device_location?: string;
  transaction_no?: string;
  commercial_sector?: string;
  registration_no?: string;
  generation_withinternalconsumption?: string;
  generation_storagesystem?: string;
  other_type_details?: string;
  other_location_details?: string;
  sk_internal_power_sitecon?: string;
  sk_external_power_sitecon?: string;
  sk_sitecon_power_subtype?: string;
  crane_in_place?: boolean;
  simultaneously_required_total_power_after_installation_change?: number;
  meter_location_designation?: string;
  reason_for_request?: string;
  sk_measuring_concept?: string;
  sk_power_meter_type?: string;
}

export interface PowerFacilityResponse {
  powerfacility: {
    facility_id: string;
  }[];
}

export interface UpdatePowerFacilityResponse {
  powerfacility: PowerFacilityData[];
}

export interface PowerSpecialConsumerData {
  specialconsumer_id?: string;
  facility_id?: string;
  sk_special_consumer_type: string;
  consumer_count: string;
  type_name: string;
  manufacturer: string;
  power: string;
  charger_controllabledevice: string;
  charger_publicdevice: string;
  other_type_details: string;
  sk_swkn_block_intervals?: string;
  dev_req_approval?: string;
  module_type?: string;
  is_controllable_facility?: string;
  is_public_facility?: string;
  zerez_id?: string;
  ski_key?: string;
  date_of_commissioning?: string;
  consumption_device_control?: string;
}

export interface PowerSpecialConsumerResponse {
  powerspecialconsumer: {
    specialconsumer_id: string;
  }[];
}

export interface UpdatePowerSpecialConsumerResponse {
  powerspecialconsumer: PowerSpecialConsumerData[];
}

export interface OldMeterData {
  id?: string;
  installation_id?: string;
  replaced_meter_no?: string;
  replaced_meter_reading?: string;
  replaced_meter_image_id?: string;
  replaced_meter_action?: string;
}

export interface OldMeterResponse {
  old_meter: {
    id: string;
  }[];
}

export interface UpdateOldMeterResponse {
  old_meter: OldMeterData[];
}

export interface PowerMeterData {
  meter_id?: string;
  installation_id?: string;
  sk_power_meter_type?: string;
  sk_meter_mount?: string;
  meter_count?: string;
  transducer_converterfactor?: string;
  transducer_customerprovided?: string;
  transducer_msbprovided?: string;
  transducer_customername?: string;
  transducer_msbname?: string;
  sk_communication?: string;
  sk_modern_meter_subtype?: string;
  sk_estimated_annual_consumption?: string;
  sk_mounting_alignment?: string;
  swkn_ctrl_unit_available?: string;
  swkn_floor_or_appnr?: string;
  old_meter_number?: string;
  sk_transducer_size_a?: string;
  rlm_measurement?: string;
  sk_measuring_concept?: string;
  max_sim_ref_power?: string;
}

export interface PowerMeterResponse {
  powermeter: {
    meter_id: string;
  }[];
}

export interface UpdatePowerMeterResponse {
  powermeter: PowerMeterData[];
}

//#endregion "data models for Power Facility forms"

//#region "data models for Water Facility forms"
export interface WaterInstallationResponse {
  waterinstallation: {
    installation_id: string;
  }[];
}

export interface WaterFacilityData {
  facility_id?: string;
  installation_id?: string;
  transaction_no: string;
  sk_water_facility_type: string;
  sk_device_location: string;
  sumflowrate: string;
  peakflowrate: string;
  commercial_sector: string;
  other_location_details: string;
  sensitive_measuring_point: boolean;
  pipe_material?: string;
  installation_type?: string;
  reflux_fuse?: string;
  devices?: string;
  other_type_detail?: string;
  reason_for_request?: string;
  heated_living_space?: number;
  volume_flow_qmax?: number;
  flow_temperature?: number;
  return_temperature?: number;
  special_structure?: string;
}

export interface WaterFacilityResponse {
  waterfacility: {
    facility_id: string;
  }[];
}

export interface UpdateWaterFacilityResponse {
  waterfacility: WaterFacilityData[];
}

export interface WaterDeviceData {
  tappingpoint_id?: string;
  facility_id?: string;
  sk_tappingpoint_type: string;
  consumer_count: string;
  vrhot: string;
  vrcold: string;
  vr_total_volume?: number;
  swkn_manufacturer?: string;
  swkn_dwgv_certified?: string;
  swkn_type?: string;
  swkn_flow_rate?: string;
  swkn_delivery_pressure?: string;
  swkn_req_extinguishing_volume?: string;
  sk_swkn_water_facility_system_type?: string;
}

export interface WaterDeviceResponse {
  watertappingpoint: {
    tappingpoint_id: string;
  }[];
}

export interface UpdateWaterDeviceResponse {
  watertappingpoint: WaterDeviceData[];
}

export interface WaterMeterData {
  meter_id?: string;
  installation_id?: string;
  meter_count: string;
  consumption_forecast: string;
  sk_water_meter_type: string;
  sk_meter_mount: string;
  sk_communication: string;
  other_size_details: string;
  sk_water_meter_size: string;
  sk_standard_compliance: string;
  sk_mounting_alignment: string;
  remote_readout?: string;
  oldMeterNumber?: string;
  location?: string;
  remote_readout_option?: string;
}

export interface WaterMeterResponse {
  watermeter: {
    meter_id: string;
  }[];
}

export interface UpdateWaterMeterResponse {
  watermeter: WaterMeterData[];
}

//#endregion "data models for Water Facility forms"

export interface TechnicalData {
  baseData: BaseDataObject[];
  meterData: MeterDataObject[];
  newFacilityData: FacilityDataObject;
  oldMeterData: MeterDataObject[];
  furnaceData: FurnaceDataObject;
  firingSystemData: FiringSystemDataObject;
  construction: any;
  exhaustSystem: any;
  fireplaceVentilation: any;
  releases: any;
  meterInstallation: any;
  chimneySweepData: any;
}

export enum ConsumptionDeviceControl {
  potentialFreeRelayContact = 'potential_free_relay_contact',
  digitalInterface = 'digital_interface',
}
